import { useState } from 'react';
import { Box, MobileStepper, IconButton, Typography } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { useSwipeable } from 'react-swipeable';
import { Photo } from '../../search/types/search.types';
import { PLACEHOLDER_IMAGES } from '@/shared/constants/placeholders';

interface PhotoCarouselProps {
  photos: Photo[];
}

export default function PhotoCarousel({ photos }: PhotoCarouselProps) {
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = photos.length;

  const handleNext = () => {
    setActiveStep(prevActiveStep => (prevActiveStep + 1) % maxSteps);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => (prevActiveStep === 0 ? maxSteps - 1 : prevActiveStep - 1));
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handleBack(),
    trackMouse: true,
  });

  if (!photos || photos.length === 0) {
    return (
      <Box
        component="img"
        src={PLACEHOLDER_IMAGES.product}
        alt="No images available"
        sx={{
          width: '100%',
          height: 300,
          borderRadius: 1,
          objectFit: 'cover',
          display: 'block',
          bgcolor: 'grey.200',
        }}
      />
    );
  }

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box {...handlers} sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box
          component="img"
          src={photos[activeStep].url}
          alt={`Photo ${activeStep + 1}`}
          sx={{
            width: '100%',
            height: 300,
            objectFit: 'cover',
            display: 'block',
            borderRadius: 1,
          }}
        />

        {maxSteps > 1 && (
          <>
            <IconButton
              onClick={handleBack}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
              aria-label="previous photo"
            >
              <KeyboardArrowLeft />
            </IconButton>

            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                },
              }}
              aria-label="next photo"
            >
              <KeyboardArrowRight />
            </IconButton>

            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 2,
              }}
            >
              <Typography variant="body2">
                {activeStep + 1} / {maxSteps}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {maxSteps > 1 && (
        <MobileStepper
          steps={maxSteps}
          position="static"
          activeStep={activeStep}
          sx={{ bgcolor: 'transparent', justifyContent: 'center', pt: 1 }}
          nextButton={<Box />}
          backButton={<Box />}
        />
      )}
    </Box>
  );
}
